<script setup lang="ts">
import { IconStar0Regular, IconStar100Regular } from "@packages/icon-library";
import { IPlaceReviewsMeta } from "@/modules/place-module";
import { computed, ComputedRef, PropType } from "vue";
import { IUser, useUserStore } from "@/modules/user-module";
import declensionWord from "../../../../package/global-helpers/declension-word";
import { useSystemStore } from "@/store/system.store";
import { useI18n } from "vue-i18n";

const props = defineProps({
  reviewsMeta: {
    type: Object as PropType<IPlaceReviewsMeta>,
    required: true,
  },
});
const emit = defineEmits(["ratePlace"]);
const { t } = useI18n();
const systemStore = useSystemStore();

const userStore = useUserStore();
const user: ComputedRef<null | IUser> = computed(() => userStore.user);

const ratePlace = (rating: number) => {
  if (!props.reviewsMeta.user_can_post_review) {
    systemStore.showNotification({
      type: "error",
      text: t("place.reviews.repeatReviewError"),
    });
    return;
  }

  emit("ratePlace", rating);
};
</script>

<template>
  <section class="place-leave-feedback">
    <div v-if="reviewsMeta?.rating" class="place-stats__header">
      <span>{{ reviewsMeta?.rating.toFixed(1) }}</span>
      <div class="place-stats__info">
        <div class="place-stats__rating">
          <template v-for="star in 5" :key="star">
            <!--              TODO ADD STARS-->
            <IconStar100Regular v-if="star <= reviewsMeta.rating" />
            <!--              <template v-else-if="star === reviewsMeta.count_ratings">-->
            <!--                <IconStar50Regular-->
            <!--                  v-if="Math.round(reviewsMeta.count_ratings) === star"-->
            <!--                />-->
            <!--                <IconStar100Regular v-else />-->
            <!--              </template>-->
            <IconStar0Regular v-else />
          </template>
        </div>
        <p>
          {{ reviewsMeta?.count_ratings }}
          {{
            declensionWord(reviewsMeta?.count_ratings, [
              t("declensions.ratings", 0),
              t("declensions.ratings", 1),
              t("declensions.ratings", 2),
            ])
          }}
          • {{ reviewsMeta?.count_reviews }}
          {{
            declensionWord(reviewsMeta?.count_reviews, [
              t("declensions.reviews", 0),
              t("declensions.reviews", 1),
              t("declensions.reviews", 2),
            ])
          }}
        </p>
      </div>
    </div>

    <template v-if="user">
      <div v-if="reviewsMeta?.is_visited" class="place-leave-feedback__stars">
        <div class="place-leave-feedback__actions">
          <button
            v-for="star in 5"
            :key="star"
            type="button"
            @click="ratePlace(star)"
          >
            <IconStar100Regular />
          </button>
        </div>

        <p v-if="reviewsMeta.user_can_post_review">
          {{ t("place.reviews.reviewInfo") }}
        </p>
      </div>

      <p v-else>
        <strong>{{ t("place.reviews.reviewImportant") }}</strong>
        {{ t("place.reviews.reviewVisit") }}
      </p>
    </template>

    <p v-else>
      <strong>{{ t("place.reviews.reviewImportant") }}</strong>
      {{ t("place.reviews.auth") }}
    </p>
  </section>
</template>

<style scoped lang="scss">
.place-leave-feedback {
  border-radius: 12px;
  background-color: var(--color-semantic-background-normal-secondary);
  padding: calc($base-space * 4);

  > p {
    margin: calc($base-space * 4) 0 0;
    @include body-s();
    color: var(--color-semantic-content-normal-tertiary);

    > strong {
      color: var(--color-semantic-content-normal-primary);
    }
  }

  &__stars {
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    gap: calc($base-space * 4);

    > p {
      text-align: center;
      @include body-s();
      color: var(--color-semantic-content-normal-secondary);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc($base-space * 3);
    margin: calc($base-space * 6) 0 0;

    @media (max-width: 1023px) {
      margin: calc($base-space * 4) 0 0;
    }

    > button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;

      @media (max-width: 1023px) {
        width: 32px;
        height: 32px;
      }

      > .icon {
        width: 40px;
        height: 40px;
        background-color: var(--color-semantic-background-active-primary);

        @media (max-width: 1023px) {
          width: 32px;
          height: 32px;
        }
      }
    }
  }

  &__empty {
    display: grid;
    grid-template-columns: 40px 1fr;
    align-items: center;
    gap: calc($base-space * 4);

    > p {
      @include body-s();
      color: var(--color-semantic-content-normal-secondary);
    }
  }
}

.place-stats {
  &__header {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: calc($base-space * 3);

    > span {
      @include h1();
      color: var(--color-semantic-content-normal-primary);
    }
  }

  &__info {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: calc($base-space * 2);

    @media (max-width: 767px) {
      gap: $base-space;
    }

    > p {
      @include body-s();
      color: var(--color-semantic-content-normal-secondary);
    }
  }

  &__rating {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: $base-space;

    > .icon {
      width: 20px;
      height: 20px;
    }

    > .icon-star_100-regular {
      background-color: var(--color-semantic-content-normal-brand);
    }

    > .icon-star_50-regular {
      background-color: var(--color-semantic-content-normal-brand);
    }

    > .icon-star_0-regular {
      background-color: var(--color-semantic-border-normal-soft);
    }
  }
}
</style>
